"use client";

import { useMemo } from "react";
import { Plus, Trash2 } from "lucide-react";
import { FgToggle } from "@/components/fg-toggle";
import { SortableList } from "../SortableList";
import { AiAssistButton } from "../AiAssistButton";
import { FormValidityBanner } from "../FormValidityBanner";
import { MobilePreview } from "../MobilePreview";

export interface MatchPair {
  id: string;
  de: string;
  en: string;
}

export interface MatchingBuilderProps {
  prompt: string;
  onPromptChange: (next: string) => void;
  pairs: MatchPair[];
  onPairsChange: (next: MatchPair[]) => void;
  /** Whether the mobile app shuffles pairs on render. */
  shuffleOnRender: boolean;
  onShuffleChange: (next: boolean) => void;
  level: "A1" | "A2" | "B1" | "B2" | "C1";
  /** Optional topic seed for the AI button. */
  topicHint?: string;
}

export function MatchingBuilder({
  prompt,
  onPromptChange,
  pairs,
  onPairsChange,
  shuffleOnRender,
  onShuffleChange,
  level,
  topicHint,
}: MatchingBuilderProps) {
  const validity = useMemo(
    () => [
      {
        label: "Prompt written",
        severity: prompt.trim() ? ("ok" as const) : ("error" as const),
      },
      {
        label: `At least 3 pairs (have ${pairs.length})`,
        severity:
          pairs.length >= 3
            ? ("ok" as const)
            : pairs.length >= 2
              ? ("warn" as const)
              : ("error" as const),
      },
      {
        label: "Every pair has both sides filled",
        severity: pairs.every((p) => p.de.trim() && p.en.trim())
          ? ("ok" as const)
          : ("error" as const),
      },
    ],
    [prompt, pairs],
  );

  function addPair() {
    onPairsChange([...pairs, { id: crypto.randomUUID(), de: "", en: "" }]);
  }

  function applyAiPairs(content: unknown) {
    if (!Array.isArray(content)) return;
    const next: MatchPair[] = [
      ...pairs,
      ...content
        .map((p) => {
          const obj = p as { de?: string; en?: string };
          return {
            id: crypto.randomUUID(),
            de: obj.de ?? "",
            en: obj.en ?? "",
          };
        })
        .filter((p) => p.de ?? p.en),
    ];
    onPairsChange(next);
  }

  return (
    <div className="flex flex-col gap-4">
      <FormValidityBanner title="Matching pairs" checks={validity} />

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.6fr,1fr]">
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">
            Prompt
          </label>
          <input
            type="text"
            value={prompt}
            onChange={(e) => onPromptChange(e.target.value)}
            placeholder="Match the German to the English."
            className="w-full h-9 rounded-md border border-[rgba(255,255,255,0.08)] bg-[#1C1C1E] px-3 text-sm text-[#FAFAFA] placeholder:text-[#8E8E93] focus:outline-none focus:border-[#FFD60A]"
          />
        </div>
        <MobilePreview
          kind="matching"
          prompt={prompt}
          items={pairs.map((p) => ({ de: p.de, en: p.en }))}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93]">
            Pairs ({pairs.length})
          </label>
          <AiAssistButton
            kind="pairs"
            prompt={topicHint ?? prompt ?? "Wortschatz"}
            level={level}
            label={`Generate 8 pairs`}
            onApply={applyAiPairs}
          />
        </div>
        <SortableList
          items={pairs}
          getKey={(p) => p.id}
          onReorder={onPairsChange}
          emptyState={
            <div className="text-center py-3">
              <p className="text-[11px] font-mono text-[#8E8E93]">
                Add at least 3 pairs — drag to reorder.
              </p>
            </div>
          }
          renderRow={({ item: p, dragHandle }) => (
            <div className="flex items-center gap-2 w-full">
              {dragHandle}
              <input
                type="text"
                value={p.de}
                onChange={(e) =>
                  onPairsChange(
                    pairs.map((x) => (x.id === p.id ? { ...x, de: e.target.value } : x)),
                  )
                }
                placeholder="Deutsch"
                aria-label={`Pair ${p.de || "new"} German side`}
                className="h-9 flex-1 rounded-md border border-[rgba(255,255,255,0.08)] bg-[#1C1C1E] px-3 text-sm text-[#FAFAFA] placeholder:text-[#8E8E93] focus:outline-none focus:border-[#FFD60A]"
              />
              <span className="text-[#8E8E93] font-mono">↔</span>
              <input
                type="text"
                value={p.en}
                onChange={(e) =>
                  onPairsChange(
                    pairs.map((x) => (x.id === p.id ? { ...x, en: e.target.value } : x)),
                  )
                }
                placeholder="English"
                aria-label={`Pair ${p.de || "new"} English side`}
                className="h-9 flex-1 rounded-md border border-[rgba(255,255,255,0.08)] bg-[#1C1C1E] px-3 text-sm text-[#FAFAFA] placeholder:text-[#8E8E93] focus:outline-none focus:border-[#FFD60A]"
              />
              <button
                type="button"
                onClick={() => onPairsChange(pairs.filter((x) => x.id !== p.id))}
                className="rounded-md p-1.5 text-[#FF453A] hover:bg-[#FF453A]/15"
                aria-label="Remove pair"
              >
                <Trash2 size={14} />
              </button>
            </div>
          )}
        />
        <button
          type="button"
          onClick={addPair}
          className="mt-3 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#1C1C1E] border border-dashed border-[rgba(255,255,255,0.12)] text-xs text-[#8E8E93] hover:text-[#FAFAFA] hover:border-[#FFD60A]/40 w-full justify-center"
        >
          <Plus size={14} />
          Add pair
        </button>
        <FgToggle
label="Shuffle pairs when learner sees the exercise"
              checked={shuffleOnRender}
              onChange={onShuffleChange}
        />
      </div>
    </div>
  );
}